import {Controller} from "@hotwired/stimulus";

// Connects to data-controller="age-restrictions"
export default class extends Controller {
  static targets = [
    "restrictionTypeSelect",
    "minimumAgeInputs",
    "adultsOnlyWarning",
    "ageRangeInputs",
    "verificationSettings",
    "checkoutVerificationField",
    "idVerificationField",
    "publicDisplayField",
    "minimumAgeField",
    "minimumAgeRangeDisplay",
    "maximumAgeRangeDisplay",
    "minimumAgeHidden",
    "maximumAgeHidden"
  ];

  connect() {
    // Initialize the form state based on current values
    this.updateVisibilityBasedOnRestrictionType();
    this.setupFieldSyncing();
    this.setupFormSubmissionHandler();
  }

  handleRestrictionChange(event) {
    const restrictionType = event.currentTarget.value;

    // Clear age values when switching to no_restrictions
    if (restrictionType === 'no_restrictions') this.clearAgeValues();

    // Hide all conditional inputs first
    this.hideAllConditionalInputs();

    // Show appropriate inputs based on selection
    switch (restrictionType) {
      case 'no_restrictions':
        this.toggleVisibility('verificationSettings', false);
        break;
      case 'minimum_age':
        this.toggleVisibility('minimumAgeInputs', true);
        this.toggleVisibility('verificationSettings', true);
        break;
      case 'adults_only':
        this.toggleVisibility('adultsOnlyWarning', true);
        this.toggleVisibility('verificationSettings', true);
        // Set minimum age to 21 for adults only
        this.setAdultsOnlyAge();
        break;
      case 'age_range':
        this.toggleVisibility('ageRangeInputs', true);
        this.toggleVisibility('verificationSettings', true);
        break;
    }
  }

  handleCheckoutVerificationChange(event) {
    this.updateFieldValue('checkoutVerificationField', event.currentTarget.checked);
  }

  handleIdVerificationChange(event) {
    this.updateFieldValue('idVerificationField', event.currentTarget.checked);
  }

  handlePublicDisplayChange(event) {
    this.updateFieldValue('publicDisplayField', event.currentTarget.checked);
  }

  // Private methods
  hideAllConditionalInputs() {
    this.toggleVisibility('minimumAgeInputs', false);
    this.toggleVisibility('adultsOnlyWarning', false);
    this.toggleVisibility('ageRangeInputs', false);
  }

  // Generic helper methods
  toggleVisibility(targetName, show) {
    switch (targetName) {
      case 'minimumAgeInputs':
        if (this.hasMinimumAgeInputsTarget) {
          this.minimumAgeInputsTarget.style.display = show ? 'block' : 'none';
        }
        break;
      case 'adultsOnlyWarning':
        if (this.hasAdultsOnlyWarningTarget) {
          this.adultsOnlyWarningTarget.style.display = show ? 'block' : 'none';
        }
        break;
      case 'ageRangeInputs':
        if (this.hasAgeRangeInputsTarget) {
          this.ageRangeInputsTarget.style.display = show ? 'block' : 'none';
        }
        break;
      case 'verificationSettings':
        if (this.hasVerificationSettingsTarget) {
          this.verificationSettingsTarget.style.display = show ? 'block' : 'none';
        }
        break;
    }
  }

  updateFieldValue(targetName, value) {
    switch (targetName) {
      case 'checkoutVerificationField':
        if (this.hasCheckoutVerificationFieldTarget) {
          this.checkoutVerificationFieldTarget.value = value;
        }
        break;
      case 'idVerificationField':
        if (this.hasIdVerificationFieldTarget) {
          this.idVerificationFieldTarget.value = value;
        }
        break;
      case 'publicDisplayField':
        if (this.hasPublicDisplayFieldTarget) {
          this.publicDisplayFieldTarget.value = value;
        }
        break;
      case 'minimumAgeField':
        if (this.hasMinimumAgeFieldTarget) {
          this.minimumAgeFieldTarget.value = value;
        }
        break;
      case 'minimumAgeHidden':
        if (this.hasMinimumAgeHiddenTarget) {
          this.minimumAgeHiddenTarget.value = value;
        }
        break;
    }
  }

  setAdultsOnlyAge() {
    // Set minimum age to 21 for adults only
    this.updateFieldValue('minimumAgeField', 21);
    this.updateFieldValue('minimumAgeHidden', 21);
    // Clear maximum age for adults only
    if (this.hasMaximumAgeHiddenTarget) {
      this.maximumAgeHiddenTarget.value = '';
    }
  }

  clearAgeValues() {
    // Clear all age-related fields
    if (this.hasMinimumAgeFieldTarget) this.minimumAgeFieldTarget.value = ''
    if (this.hasMinimumAgeHiddenTarget) this.minimumAgeHiddenTarget.value = ''
    if (this.hasMaximumAgeHiddenTarget)  this.maximumAgeHiddenTarget.value = ''
    if (this.hasMinimumAgeRangeDisplayTarget) this.minimumAgeRangeDisplayTarget.value = ''
    if (this.hasMaximumAgeRangeDisplayTarget) this.maximumAgeRangeDisplayTarget.value = ''
  }

  setupFieldSyncing() {
    // Sync minimum age field with hidden field
    if (this.hasMinimumAgeFieldTarget) {
      this.minimumAgeFieldTarget.addEventListener('input', (e) => {
        const sanitizedValue = this.sanitizeAgeInput(e.target.value);
        e.target.value = sanitizedValue;
        if (this.hasMinimumAgeHiddenTarget) {
          this.minimumAgeHiddenTarget.value = sanitizedValue;
        }
      });
    }

    // Sync age range display fields with hidden fields
    if (this.hasMinimumAgeRangeDisplayTarget) {
      this.minimumAgeRangeDisplayTarget.addEventListener('input', (e) => {
        const sanitizedValue = this.sanitizeAgeInput(e.target.value);
        e.target.value = sanitizedValue;
        if (this.hasMinimumAgeHiddenTarget) {
          this.minimumAgeHiddenTarget.value = sanitizedValue;
        }
      });
    }

    if (this.hasMaximumAgeRangeDisplayTarget) {
      this.maximumAgeRangeDisplayTarget.addEventListener('input', (e) => {
        const sanitizedValue = this.sanitizeAgeInput(e.target.value);
        e.target.value = sanitizedValue;
        if (this.hasMaximumAgeHiddenTarget) {
          this.maximumAgeHiddenTarget.value = sanitizedValue;
        }
      });
    }
  }

  sanitizeAgeInput(value) {
    // Remove any non-numeric characters
    let sanitized = value.replace(/[^\d]/g, '');

    // Convert to integer and validate range
    let numericValue = parseInt(sanitized, 10);

    // Handle empty or invalid input
    if (isNaN(numericValue) || sanitized === '') {
      return '';
    }

    // Enforce minimum and maximum age limits
    if (numericValue < 1) {
      numericValue = 1;
    } else if (numericValue > 99) {
      numericValue = 99;
    }

    return numericValue.toString();
  }

  updateVisibilityBasedOnRestrictionType() {
    // Find the selected option in the dropdown to determine current state
    if (this.hasRestrictionTypeSelectTarget && this.restrictionTypeSelectTarget.value) {
      // Now we pass the actual select element as currentTarget so handleRestrictionChange can access all its properties
      const syntheticEvent = {
        currentTarget: this.restrictionTypeSelectTarget,
        synthetic: true
      };
      this.handleRestrictionChange(syntheticEvent);
    }
  }

  setupFormSubmissionHandler() {
    // Find the form that contains this age restrictions section
    const form = this.element.closest('form');
    if (form) {
      form.addEventListener('submit', (e) => {
        this.syncAllFieldsBeforeSubmission();
      });
    }
  }

  syncAllFieldsBeforeSubmission() {
    // Sync all display fields to hidden fields before form submission
    // This ensures the latest values are captured
    // IMPORTANT: This method ensures that only relevant age values are submitted based on the restriction type
    // For example, if 'minimum_age' is selected, we clear the maximum_age value to avoid validation errors

    const restrictionType = this.hasRestrictionTypeSelectTarget ? this.restrictionTypeSelectTarget.value : 'no_restrictions';

    // Clear values based on restriction type to match Rails model validations
    switch(restrictionType) {
      case 'no_restrictions':
        // Clear all age values
        if (this.hasMinimumAgeHiddenTarget) this.minimumAgeHiddenTarget.value = '';
        if (this.hasMaximumAgeHiddenTarget) this.maximumAgeHiddenTarget.value = '';
        break;
      
      case 'minimum_age':
        // Sync minimum age from the visible field
        if (this.hasMinimumAgeFieldTarget && this.hasMinimumAgeHiddenTarget) {
          this.minimumAgeHiddenTarget.value = this.minimumAgeFieldTarget.value;
        }
        // Clear maximum age
        if (this.hasMaximumAgeHiddenTarget) this.maximumAgeHiddenTarget.value = '';
        break;
      
      case 'adults_only':
        // Ensure minimum age is 21
        if (this.hasMinimumAgeHiddenTarget) {
          this.minimumAgeHiddenTarget.value = '21';
        }
        // Clear maximum age
        if (this.hasMaximumAgeHiddenTarget) this.maximumAgeHiddenTarget.value = '';
        break;
      
      case 'age_range':
        // Sync both minimum and maximum age from range fields
        if (this.hasMinimumAgeRangeDisplayTarget && this.hasMinimumAgeHiddenTarget) {
          this.minimumAgeHiddenTarget.value = this.minimumAgeRangeDisplayTarget.value;
        }
        if (this.hasMaximumAgeRangeDisplayTarget && this.hasMaximumAgeHiddenTarget) {
          this.maximumAgeHiddenTarget.value = this.maximumAgeRangeDisplayTarget.value;
        }
        break;
    }
  }
}